import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import axios from "./axios";

// ========================================
// TYPES
// ========================================

export type BrandType = "auto" | "moto";

export interface Brand {
  id: number;
  name: string;
  uid: string;
  type: BrandType;
  logo_url: string | null;
}

export interface BrandModel {
  id: number;
  name: string;
  brand?: { id: number; name: string };
}

export interface CreateBrandRequest {
  name: string;
  type: BrandType;
  uid?: string;
  logo_url?: string | null;
}

export interface UpdateBrandRequest extends Partial<CreateBrandRequest> {}

interface PaginatedBrands {
  data: Brand[];
  total: number;
  current_page: number;
  last_page: number;
  per_page: number;
}

// ========================================
// QUERY KEYS
// ========================================

export const brandKeys = {
  all: ["brands"] as const,
  list: (type: string, page: number, per_page: number, filter: string) =>
    ["brands", "list", type, page, per_page, filter] as const,
  models: (brandId: number) => ["brand-models", brandId] as const,
};

// ========================================
// QUERIES
// ========================================

/**
 * Paginated brands, filtered by type (auto/moto) and an optional name search.
 * Mirrors the backend path-param list endpoint:
 *   /brand/list/type/{type}/{page}/{per_page}[/{filter}]
 */
export const useGetAllBrands = (
  type: BrandType = "auto",
  page: number = 1,
  per_page: number = 10,
  filter: string = ""
) => {
  const { data, ...res } = useQuery({
    queryKey: brandKeys.list(type, page, per_page, filter),
    queryFn: async (): Promise<PaginatedBrands> => {
      const base = `/brand/list/type/${type}/${page}/${per_page}`;
      const path = filter.trim().length
        ? `${base}/${encodeURIComponent(filter.trim())}`
        : base;
      const response = await axios.get<PaginatedBrands>(path);
      return response.data;
    },
    staleTime: 0,
  });

  return {
    brands: data?.data ?? [],
    totalCount: data?.total ?? 0,
    ...res,
  };
};

/** Models belonging to a brand. */
export const useGetBrandModels = (brandId: number, enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: brandKeys.models(brandId),
    queryFn: async (): Promise<BrandModel[]> => {
      const response = await axios.get<BrandModel[]>(`/brand/model/${brandId}`);
      return response.data;
    },
    enabled: enabled && !!brandId && brandId > 0,
  });

  return { models: data ?? [], ...res };
};

// ========================================
// MUTATIONS — brands
// ========================================

export const useCreateBrand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateBrandRequest): Promise<Brand> => {
      const response = await axios.post<Brand>("/brand", payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandKeys.all });
    },
    onError: (err: any) => {
      toast.error(
        firstError(err) || "Erreur lors de la création de la marque"
      );
    },
  });
};

export const useUpdateBrand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateBrandRequest;
    }): Promise<Brand> => {
      const response = await axios.put<Brand>(`/brand/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandKeys.all });
    },
    onError: (err: any) => {
      toast.error(firstError(err) || "Erreur lors de la mise à jour");
    },
  });
};

export const useDeleteBrand = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(`/brand/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandKeys.all });
    },
    onError: (err: any) => {
      toast.error(firstError(err) || "Erreur lors de la suppression");
    },
  });
};

// ========================================
// MUTATIONS — brand models
// ========================================

export const useCreateBrandModel = (brandId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<BrandModel> => {
      const response = await axios.post<BrandModel>(
        `/brand/${brandId}/model`,
        { name }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandKeys.models(brandId) });
    },
    onError: (err: any) => {
      toast.error(firstError(err) || "Erreur lors de la création du modèle");
    },
  });
};

export const useUpdateBrandModel = (brandId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
    }: {
      id: number;
      name: string;
    }): Promise<BrandModel> => {
      const response = await axios.put<BrandModel>(`/brand/model/${id}`, {
        name,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandKeys.models(brandId) });
    },
    onError: (err: any) => {
      toast.error(firstError(err) || "Erreur lors de la mise à jour du modèle");
    },
  });
};

export const useDeleteBrandModel = (brandId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(`/brand/model/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandKeys.models(brandId) });
    },
    onError: (err: any) => {
      toast.error(firstError(err) || "Erreur lors de la suppression du modèle");
    },
  });
};

// ========================================
// helpers
// ========================================

/** Surface Laravel's first field-level validation message when present. */
function firstError(err: any): string {
  const res = err?.response?.data;
  if (res?.errors) {
    const values = Object.values(res.errors).flat() as string[];
    if (values.length) return values.join(" ");
  }
  return res?.message ?? "";
}
